import React, { useCallback, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { isEqual } from 'lodash';
import {
  Card,
  DatePicker,
  Form,
  Input,
  notification,
  Popconfirm,
  Button as AntButton,
} from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import DataGrid from 'components/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import { indexBy, setValueByNamePath, toDatePickerDate } from 'utils/utils2';
import { useFormList } from 'hooks/useFormList';
import ParListSelect from 'components/ParListSelect';
import DeleteButton from 'components/DeleteButton';
import FormInput from 'components/FormInput';
import ButtonOperation from 'components/ButtonOperation';
// import ThirdPartyInput from 'components/ThirdPartyInput';
import Title from 'components/Title';
import moment from 'moment';
import {
  AssetLinks,
  AssetLinksResponse,
  addAssetLinks,
  deleteThirdPartyReltion,
  getAssetLinksListByAssetid,
  updateThirdPartyReltion,
} from 'services/assets';
import SaveButtonIcon from 'components/SaveBtn';
import UpdateButton from 'components/UpdateButtonAction';
import AssetSelect from 'components/AssetSelect';

const NEW_LINK_ID = -1;

export type AssetLinksListCardProps = {
  assetId: number;
  showAsModal?: boolean;
  refetchTab?: boolean;
};
type Values = {
  AssetLink: AssetLinks[];
};

const AssetLinksListCard = (props: AssetLinksListCardProps) => {
  const { assetId, showAsModal, refetchTab } = props;
  const [form] = Form.useForm();
  const fieldName = 'AssetLinks';
  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'linkid',
    addAsFirst: true,
  });

  const { data, refetch } = useQuery(
    ['getAssetLinksListByAssetid', { assetid: Number(assetId) }],
    () =>
      getAssetLinksListByAssetid({ assetid: Number(assetId) }).then(
        (res) => res.list || []
      ),
    { enabled: !!assetId }
  );

  useEffect(() => {
    refetch();
    if (data) {
      form.setFieldsValue({
        [fieldName]: data.map((asset: AssetLinksResponse) => ({
          ...asset,
          startdate: toDatePickerDate(asset.startdate),
          enddate: toDatePickerDate(asset.enddate),
          relvalue:
            asset.reltype === 'ASSET'
              ? {
                  assetid: asset.assetidtarget,
                  assetreference: asset.relvalue,
                  assetlabel: asset.assetlabel,
                }
              : asset.relvalue,
        })),
      });
    } else {
      form.setFieldsValue({
        [fieldName]: [],
      });
    }
  }, [form, data, refetchTab, refetch]);

  const onSaveOrEdit = useCallback(
    (record: any) => {
      const asset: AssetLinks[] = form.getFieldValue(fieldName);
      const index = indexBy(asset, 'linkid')[record.linkid];
      const currentValue = {
        ...asset[index],
      };
      const assetrecord: {
        assetid: number;
        assetreference: string;
        assetlabel: string;
      } = record.relvalue;

      const { linkid } = currentValue;
      linkid === NEW_LINK_ID
        ? addAssetLinks({
            request: {
              ...currentValue,
              assetid: assetId,
              relvalue:
                currentValue.reltype === 'ASSET'
                  ? assetrecord.assetreference
                  : record.relvalue,
              assetidtarget:
                currentValue.reltype === 'ASSET' ? assetrecord.assetid : 0,
            },
            assetid: assetId,
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          })
        : updateThirdPartyReltion({
            linkid: currentValue.linkid,
            request: {
              ...currentValue,
              assetid: assetId,
              relvalue:
                currentValue.reltype === 'ASSET'
                  ? assetrecord.assetreference
                  : record.relvalue,
              assetidtarget:
                currentValue.reltype === 'ASSET' ? assetrecord.assetid : 0,
            },
            assetid: assetId,
          }).then((response) => {
            if (response.code === '200') {
              notification.success({
                message: response.message,
              });
              refetch();
            }
          });
    },
    [form, refetch, assetId]
  );
  const getName = useCallback(
    (name: string) => (record: AssetLinks) => {
      const functions = form.getFieldValue(fieldName);
      const index = indexBy(functions, 'linkid')[record.linkid];

      return [fieldName, index, name];
    },
    [form]
  );
  const columns = useMemo<DataGridColDef<AssetLinksResponse>[]>(
    () => [
      {
        dataIndex: 'reltype',
        render: (_value, record, index) => (
          <Form.Item>
            <FormInput noStyle name={getName('reltype')(record)}>
              <ParListSelect
                parcode="ASSRELTYPE"
                onSelect={(e) => {
                  if (
                    (e === 'TAG' || e === 'FREE') &&
                    record.reltype === 'ASSET'
                  ) {
                    setValueByNamePath(
                      form,
                      [fieldName, index, 'relvalue'],
                      ''
                    );
                  } else {
                    if (e === 'ASSET' && record.reltype === 'ASSET') {
                      setValueByNamePath(form, [fieldName, index, 'relvalue'], {
                        assetid: record.assetidtarget,
                        assetreference: record.relvalue,
                        assetlabel: record.assetlabel,
                      });
                    } else if (
                      (e === 'TAG' || e === 'FREE') &&
                      !(record.reltype === 'ASSET')
                    ) {
                      setValueByNamePath(
                        form,
                        [fieldName, index, 'relvalue'],
                        form.getFieldValue('relvalue')
                      );
                    }
                  }
                }}
              />
            </FormInput>
          </Form.Item>
        ),
      },

      {
        dataIndex: 'relcode',
        render: (_value, record, index) => (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => (
              <FormInput
                name={getName('relcode')(record)}
                rules={[{ required: true }]}
                noStyle
              >
                <ParListSelect
                  parcode="ASSRELCODE"
                  parvalueparent={getFieldValue(getName('reltype')(record))}
                />
              </FormInput>
            )}
          </Form.Item>
        ),
      },
      {
        dataIndex: 'relvalue',
        render: (_value, record, index) => (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const reltype = getFieldValue(getName('reltype')(record));
              var reltypeValue;
              if (reltype === 'ASSET') {
                reltypeValue = <AssetSelect onlyAssetId />;
              }
              if (reltype === 'TAG') {
                reltypeValue = (
                  <ParListSelect
                    parcode={getFieldValue(getName('relcode')(record))}
                  />
                );
              }
              if (reltype === 'FREE') {
                reltypeValue = <Input />;
              }

              return (
                <FormInput
                  name={getName('relvalue')(record)}
                  rules={[{ required: true }]}
                  noStyle
                >
                  {reltypeValue}
                </FormInput>
              );
            }}
          </Form.Item>
        ),
      },
      {
        dataIndex: 'startdate',
        render: (_value, record, index) => (
          <Form.Item name={getName('startdate')(record)}>
            <DatePicker defaultValue={moment()} />
          </Form.Item>
        ),
      },
      {
        dataIndex: 'enddate',
        render: (_value, record, index) => (
          <Form.Item name={getName('enddate')(record)}>
            <DatePicker />
          </Form.Item>
        ),
      },
      {
        key: 'actions',
        render: (_, _record) =>
          _record.linkid === NEW_LINK_ID ? (
            <>
              <UpdateButton
                onClick={(ev) => {
                  ev.preventDefault();
                  onSaveOrEdit(_record);
                }}
                comcode="save"
              />
              <AntButton
                danger
                htmlType="button"
                icon={
                  <CloseOutlined height="14px" width="14px" color="white" />
                }
                onClick={() => {
                  remove(_record.linkid);
                }}
              />
            </>
          ) : (
            <>
              <SaveButtonIcon
                onClick={(ev) => {
                  ev.preventDefault();
                  onSaveOrEdit(_record);
                }}
                onlyIcon
              />
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() => {
                  deleteThirdPartyReltion({
                    linkid: _record.linkid,
                    assetid: assetId,
                  }).then((res) => {
                    notification.success({ message: res.message });
                    refetch();
                  });
                }}
              >
                <DeleteButton onlyIcon />
              </Popconfirm>
            </>
          ),
        width: 200,
        fixed: 'right',
      },
    ],

    [form, getName, onSaveOrEdit, refetch, remove]
  );

  const addRow = () => {
    add(row);
  };

  return (
    <Card
      style={{
        backgroundColor: '#f7f7f7',
        boxShadow:
          'rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px',
        padding: 12,
      }}
      bordered
      title={<Title comcode="AssetLinks" level={5}></Title>}
    >
      <Form form={form} initialValues={row}>
        <Form.Item
          noStyle
          shouldUpdate={(prevValue: Values, nextValue: Values) => {
            return !isEqual(prevValue, nextValue);
          }}
        >
          {({ getFieldValue }) => {
            const asset: AssetLinksResponse[] = getFieldValue(fieldName);
            const isNewItemInTable =
              asset?.find(({ linkid }) => linkid === NEW_LINK_ID) !== undefined;
            return (
              <DataGrid
                rowKey="id"
                dataSource={asset}
                columns={
                  !showAsModal
                    ? columns
                    : columns.filter((cl) => cl.key !== 'actions')
                }
                hideFilters
                extraActions={
                  !showAsModal && (
                    <ButtonOperation
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={addRow}
                      disabled={isNewItemInTable}
                      comcode="add"
                    />
                  )
                }
              />
            );
          }}
        </Form.Item>
      </Form>
    </Card>
  );
};

const row: AssetLinksResponse = {
  assetid: 0,
  assetidtarget: 0,
  assetlabel: '',
  assettargetlabel: '',
  creationdate: '',
  enddate: '',
  linkid: NEW_LINK_ID,
  relcode: '',
  reltype: '',
  relvalue: '',
  startdate: '',
  updatedate: '',
  useridcreate: 0,
  useridupdate: 0,
};

export default AssetLinksListCard;
